// C2 — Programación Semanal de Líderes (coordinador).
//
// Refactor visual 2026-05-26 alineado al diseño piloto UPG:
//   - Layout 2 columnas: panel "Líderes disponibles" + grid central
//   - Grid filas = líneas, columnas = 7 días, cada celda con 3 sub-zonas T1/T2/T3
//   - Drag & drop: arrastrar card de líder → soltar en sub-zona
//   - Hover sobre celda asignada → popover con detalle + Quitar/Cambiar
//   - Header con métricas en tiempo real (Asignadas, Líneas completas, Conflictos)
//   - Acciones: Esta semana, Copiar semana anterior, Importar Excel (F2), Publicar (ceremonial)
//
// Decisiones UX:
//   - Drop en celda OCUPADA → toast bloqueando ("Quitar primero"). Evita
//     borrados accidentales en piloto. El usuario puede usar el popover Quitar/Cambiar.
//   - Drop del MISMO líder en su celda actual → no-op (sin error).

import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  ConflictoProgramacion,
  CoordinadorProgramacionSemana,
  LiderEnriquecido,
  ProgramacionLiderEnriquecida,
} from 'schemas';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Popover } from '../../components/ui/popover';
import { Spinner } from '../../components/ui/spinner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { ApiError } from '../../lib/api/client';
import {
  useBorrarProgramacionLider,
  useCoordinadorProgramacionSemana,
  useCopiarSemanaAnterior,
  useCrearProgramacionLider,
  useMoverProgramacionLider,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { textoOmitidasDiaSinTurno } from '../../lib/textoOmitidasDiaSinTurno';
import { C2ImportarLideresDialog } from './C2ImportarLideresDialog';
import { CeldaNoOperaTooltip } from './CeldaNoOperaTooltip';

// =============================================================================
// Helpers
// =============================================================================

const FMT_DIA_SEMANA = new Intl.DateTimeFormat('es-CO', { weekday: 'short', timeZone: 'UTC' });

/** Nombre corto del día de una fecha ISO ('2026-06-29' → 'lun'). Deriva del
 *  día REAL, así el grid funciona con cualquier inicio de semana operativa. */
function diaSemanaCorto(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const [y, m, d] = fechaIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return FMT_DIA_SEMANA.format(new Date(Date.UTC(y, m - 1, d))).replace(/\./g, '');
}

function formatearFechaCorta(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const parts = fechaIso.slice(0, 10).split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

function semanaIsoOffset(iso: string, deltaSemanas: number): string {
  // Calcula iso ± N semanas usando el lunes de la semana.
  const m = iso.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return iso;
  const año = Number(m[1]);
  const semana = Number(m[2]);
  // ISO 8601: lunes de semana 1 = 4 de enero (mismo cálculo que backend)
  const ene4 = new Date(Date.UTC(año, 0, 4));
  const dow = ene4.getUTCDay() || 7;
  const lunesSem1 = new Date(ene4);
  lunesSem1.setUTCDate(ene4.getUTCDate() - (dow - 1));
  const lunes = new Date(lunesSem1);
  lunes.setUTCDate(lunesSem1.getUTCDate() + (semana - 1 + deltaSemanas) * 7);
  // Recalcular semana_iso del lunes resultante
  const tmp = new Date(Date.UTC(lunes.getUTCFullYear(), lunes.getUTCMonth(), lunes.getUTCDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const wn = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`;
}

type CeldaKey = `${string}|${string}|${string}`; // fecha|turno_id|linea_id

function celdaKey(fecha: string, turnoId: string, lineaId: string): CeldaKey {
  return `${fecha}|${turnoId}|${lineaId}`;
}

// =============================================================================
// Componente principal
// =============================================================================

export function C2ProgramacionLideresPage() {
  const { state } = useAuth();
  const [semanaIso, setSemanaIso] = useState<string | null>(null);
  const { data, isLoading, isError, error } = useCoordinadorProgramacionSemana(semanaIso);
  const [draggedLiderId, setDraggedLiderId] = useState<string | null>(null);
  const [draggedProgramacionId, setDraggedProgramacionId] = useState<string | null>(null);
  const [importarOpen, setImportarOpen] = useState(false);

  const crearMut = useCrearProgramacionLider();
  const moverMut = useMoverProgramacionLider();
  const borrarMut = useBorrarProgramacionLider();
  const copiarMut = useCopiarSemanaAnterior();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando programación…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar la programación</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  function handleDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    if (id.startsWith('lider-')) {
      setDraggedLiderId(id.substring('lider-'.length));
    } else if (id.startsWith('prog-')) {
      setDraggedProgramacionId(id.substring('prog-'.length));
    }
  }

  function handleDragEnd(e: DragEndEvent) {
    setDraggedLiderId(null);
    setDraggedProgramacionId(null);
    const overId = e.over?.id ? String(e.over.id) : null;
    const activeId = String(e.active.id);
    if (!overId || !overId.startsWith('drop-')) return;
    // overId formato: drop-<fecha>-<turnoId>-<lineaId>
    const partsRaw = overId.substring('drop-'.length).split('|');
    const [fecha, turnoId, lineaId] = partsRaw;
    if (!fecha || !turnoId || !lineaId) return;

    // ¿Celda ocupada? Si sí, bloquear (decisión UX: no borrar accidentalmente)
    const ocupada = data!.programaciones.find(
      (p) => p.fecha === fecha && p.turno_id === turnoId && p.linea_id === lineaId,
    );

    if (activeId.startsWith('lider-')) {
      // Drop desde panel: crear nueva programación
      const liderId = activeId.substring('lider-'.length);
      if (ocupada) {
        if (ocupada.lider_user_id === liderId) return; // no-op: mismo líder ya está
        toast.error('Celda ocupada. Quitar el líder existente antes de asignar uno nuevo.');
        return;
      }
      crearMut.mutate(
        {
          fecha,
          turno_id: turnoId,
          linea_id: lineaId,
          lider_user_id: liderId,
          es_linea_principal: true,
        },
        {
          onSuccess: () => toast.success('Líder asignado'),
          onError: (err) => {
            if (err instanceof ApiError) toast.error(err.message);
            else toast.error('Error al asignar');
          },
        },
      );
    } else if (activeId.startsWith('prog-')) {
      // Drop desde otra celda: mover programación
      const progId = activeId.substring('prog-'.length);
      const original = data!.programaciones.find((p) => p.id === progId);
      if (!original) return;
      // Si destino es la celda original → no-op
      if (
        original.fecha === fecha &&
        original.turno_id === turnoId &&
        original.linea_id === lineaId
      ) {
        return;
      }
      if (ocupada) {
        toast.error('Celda destino ocupada. Quitar el líder existente primero.');
        return;
      }
      moverMut.mutate(
        { id: progId, patch: { fecha, turno_id: turnoId, linea_id: lineaId } },
        {
          onSuccess: () => toast.success('Programación movida'),
          onError: (err) => {
            if (err instanceof ApiError) toast.error(err.message);
            else toast.error('Error al mover');
          },
        },
      );
    }
  }

  function handleQuitar(progId: string) {
    borrarMut.mutate(progId, {
      onSuccess: () => toast.success('Líder removido'),
      onError: (err) => {
        if (err instanceof ApiError) toast.error(err.message);
        else toast.error('Error al quitar');
      },
    });
  }

  function handleCopiarSemanaAnterior() {
    copiarMut.mutate(
      { semana_iso_destino: data!.semana_iso },
      {
        onSuccess: (r) => {
          toast.success(`Copiadas ${r.filas_creadas} asignaciones desde ${r.semana_iso_origen}`);
          const textoOmitidas = textoOmitidasDiaSinTurno(r.omitidas_dia_sin_turno);
          if (textoOmitidas) toast.info(textoOmitidas);
        },
        onError: (err) => {
          if (err instanceof ApiError) toast.error(err.message);
          else toast.error('Error al copiar');
        },
      },
    );
  }

  function handlePublicar() {
    // Ceremonial: solo confirma visualmente. No persiste estado.
    toast.success(`Programación de ${data!.semana_iso} lista para captura`);
  }

  const draggedLider = draggedLiderId
    ? data.lideres_enriquecidos.find((l) => l.id === draggedLiderId)
    : null;
  const draggedProgramacion = draggedProgramacionId
    ? data.programaciones.find((p) => p.id === draggedProgramacionId)
    : null;

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-col gap-4">
          {/* ===== Header ribbon ===== */}
          <HeaderRibbon
            data={data}
            onSemanaIsoChange={setSemanaIso}
            onCopiarSemanaAnterior={handleCopiarSemanaAnterior}
            onPublicar={handlePublicar}
            onImportar={() => setImportarOpen(true)}
            isCopiando={copiarMut.isPending}
          />

          {/* ===== Layout 2 columnas ===== */}
          {data.scope.plantas_asignadas.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                Sin plantas asignadas. Contacte al admin del tenant.
              </CardContent>
            </Card>
          ) : data.lineas.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                Sin líneas activas en tu scope.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
              <LideresDisponiblesPanel data={data} />
              <GridSemanal data={data} onQuitar={handleQuitar} />
            </div>
          )}

          <footer className="text-center text-[11px] text-fg-tertiary">
            C2 · semana {data.semana_iso} · {data.metricas.celdas_asignadas}/
            {data.metricas.celdas_total} celdas asignadas
          </footer>
        </div>

        {importarOpen ? (
          <C2ImportarLideresDialog data={data} onClose={() => setImportarOpen(false)} />
        ) : null}

        {/* Drag overlay: muestra "Asignar X a ..." mientras arrastras */}
        <DragOverlay dropAnimation={null}>
          {draggedLider ? (
            <div className="pointer-events-none rounded-md border border-accent bg-surface-elevated px-3 py-2 shadow-xl">
              <div className="flex items-center gap-2">
                <span className="flex size-7 items-center justify-center rounded-full bg-accent text-[11px] font-semibold text-fg-on-accent">
                  {draggedLider.iniciales}
                </span>
                <span className="text-sm font-medium text-fg">
                  {draggedLider.nombre} {draggedLider.apellido}
                </span>
              </div>
            </div>
          ) : draggedProgramacion ? (
            <div className="pointer-events-none rounded-md border border-accent bg-surface-elevated px-3 py-2 shadow-xl">
              <span className="text-sm font-medium text-fg">
                {draggedProgramacion.lider_nombre} {draggedProgramacion.lider_apellido}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </RoleLayout>
  );
}

// =============================================================================
// HeaderRibbon — semana + métricas + acciones
// =============================================================================

function HeaderRibbon({
  data,
  onSemanaIsoChange,
  onCopiarSemanaAnterior,
  onPublicar,
  onImportar,
  isCopiando,
}: {
  data: CoordinadorProgramacionSemana;
  onSemanaIsoChange: (iso: string | null) => void;
  onCopiarSemanaAnterior: () => void;
  onPublicar: () => void;
  onImportar: () => void;
  isCopiando: boolean;
}) {
  // Primer y último día de la semana OPERATIVA (el backend manda el orden).
  const inicioSemana = data.dias[0]!;
  const finSemana = data.dias[6]!;
  const semanaNum = data.semana_iso.split('-W')[1];

  return (
    <header className="flex flex-col gap-3 border-b border-border pb-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-medium text-fg">
            Semana {semanaNum} · {formatearFechaCorta(inicioSemana)} →{' '}
            {formatearFechaCorta(finSemana)} ·{' '}
            <span className="font-mono text-base text-fg-tertiary">{inicioSemana.slice(0, 4)}</span>
          </h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {data.tenant.nombre} · {data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSemanaIsoChange(semanaIsoOffset(data.semana_iso, -1))}
          >
            ← Anterior
          </Button>
          <Button variant="outline" size="sm" onClick={() => onSemanaIsoChange(null)}>
            Esta semana
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSemanaIsoChange(semanaIsoOffset(data.semana_iso, 1))}
          >
            Siguiente →
          </Button>
        </div>
      </div>

      {/* Métricas + acciones */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <MetricaInline
            label="Asignadas"
            value={`${data.metricas.celdas_asignadas} / ${data.metricas.celdas_total}`}
            tone={data.metricas.celdas_asignadas === data.metricas.celdas_total ? 'ok' : 'neutral'}
          />
          <MetricaInline
            label="Líneas completas"
            value={`${data.metricas.lineas_completas_count} / ${data.metricas.lineas_total}`}
            tone={
              data.metricas.lineas_completas_count === data.metricas.lineas_total ? 'ok' : 'neutral'
            }
          />
          {/* Doble asignación = líder en 2+ líneas del mismo turno. Es un caso
              VÁLIDO (líneas auxiliares, ver spec del rol Líder §2) → dato
              informativo, nunca alerta. */}
          <MetricaInline
            label="Doble asignación"
            value={String(data.metricas.conflictos_count)}
            tone="neutral"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!data.semana_anterior_tiene_data || isCopiando}
            onClick={onCopiarSemanaAnterior}
          >
            {isCopiando ? 'Copiando…' : 'Copiar semana anterior'}
          </Button>
          <Button variant="outline" size="sm" onClick={onImportar}>
            Importar Excel
          </Button>
          <Button size="sm" onClick={onPublicar}>
            Publicar programación
          </Button>
        </div>
      </div>
    </header>
  );
}

function MetricaInline({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'ok' | 'crit' | 'neutral';
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span
        className={cn(
          'text-sm font-medium tabular-nums',
          tone === 'ok' ? 'text-ok' : tone === 'crit' ? 'text-crit' : 'text-fg',
        )}
      >
        {value}
      </span>
    </div>
  );
}

// =============================================================================
// LideresDisponiblesPanel — sidebar izquierdo
// =============================================================================

function LideresDisponiblesPanel({ data }: { data: CoordinadorProgramacionSemana }) {
  const [search, setSearch] = useState('');
  const [plantaFiltro, setPlantaFiltro] = useState<string>('todas');
  const plantas = data.scope.plantas_asignadas;

  const lideresFiltrados = useMemo(() => {
    const s = search.trim().toLowerCase();
    let arr = [...data.lideres_enriquecidos];
    if (plantaFiltro !== 'todas') {
      arr = arr.filter((l) => l.planta_principal_id === plantaFiltro);
    }
    if (s) {
      arr = arr.filter(
        (l) =>
          l.nombre.toLowerCase().includes(s) ||
          l.apellido.toLowerCase().includes(s) ||
          l.email.toLowerCase().includes(s),
      );
    }
    return arr;
  }, [data.lideres_enriquecidos, search, plantaFiltro]);

  return (
    <aside className="flex flex-col gap-3 self-start lg:sticky lg:top-4">
      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          <header className="flex items-baseline justify-between gap-2">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Líderes disponibles
            </h2>
            <span className="text-[11px] text-fg-tertiary">{lideresFiltrados.length}</span>
          </header>
          <Input
            placeholder="Buscar líder…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {plantas.length > 1 ? (
            <div className="flex flex-wrap gap-1">
              <TabPlanta active={plantaFiltro === 'todas'} onClick={() => setPlantaFiltro('todas')}>
                Todas
              </TabPlanta>
              {plantas.map((p) => (
                <TabPlanta
                  key={p.id}
                  active={plantaFiltro === p.id}
                  onClick={() => setPlantaFiltro(p.id)}
                >
                  {p.codigo}
                </TabPlanta>
              ))}
            </div>
          ) : null}
          <ul
            className="flex max-h-[calc(100vh-300px)] flex-col gap-1.5 overflow-y-auto pr-1"
            role="list"
          >
            {lideresFiltrados.length === 0 ? (
              <li className="py-4 text-center text-[12px] text-fg-tertiary">Sin resultados</li>
            ) : (
              lideresFiltrados.map((l) => <LiderCard key={l.id} lider={l} />)
            )}
          </ul>
        </CardContent>
      </Card>
    </aside>
  );
}

function TabPlanta({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
        active
          ? 'bg-accent text-fg-on-accent'
          : 'bg-surface-overlay text-fg-secondary hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}

function LiderCard({ lider }: { lider: LiderEnriquecido }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `lider-${lider.id}`,
  });
  return (
    <li
      ref={setNodeRef}
      className={cn(
        'group flex cursor-grab items-center gap-2 rounded-md border border-border bg-surface-base p-2 transition-shadow hover:border-fg-tertiary active:cursor-grabbing',
        isDragging && 'opacity-50',
      )}
      {...listeners}
      {...attributes}
    >
      <span className="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-surface-overlay text-[11px] font-semibold text-fg">
        {lider.iniciales}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[13px] font-medium text-fg">
          {lider.nombre} {lider.apellido}
        </span>
        <span className="truncate text-[10.5px] text-fg-tertiary">
          {lider.planta_principal_nombre ?? 'Sin planta'} ·{' '}
          <span className="tabular-nums">{lider.asignaciones_count}</span> asignacion
          {lider.asignaciones_count === 1 ? '' : 'es'}
        </span>
      </div>
      <span className="text-fg-tertiary opacity-0 transition-opacity group-hover:opacity-100">
        ⋮⋮
      </span>
    </li>
  );
}

// =============================================================================
// GridSemanal — filas líneas, columnas días, cada celda con 3 sub-celdas turno
// =============================================================================

function GridSemanal({
  data,
  onQuitar,
}: {
  data: CoordinadorProgramacionSemana;
  onQuitar: (progId: string) => void;
}) {
  const progPorCelda = useMemo(() => {
    const map = new Map<CeldaKey, ProgramacionLiderEnriquecida>();
    for (const p of data.programaciones) {
      map.set(celdaKey(p.fecha, p.turno_id, p.linea_id), p);
    }
    return map;
  }, [data.programaciones]);

  // Conflictos por (fecha, turno, lider): set de keys
  const conflictosByKey = useMemo(() => {
    const set = new Set<string>();
    for (const c of data.conflictos) {
      set.add(`${c.fecha}|${c.turno_id}|${c.lider_user_id}`);
    }
    return set;
  }, [data.conflictos]);

  const turnosOrdenados = useMemo(
    () => [...data.turnos].sort((a, b) => a.numero - b.numero),
    [data.turnos],
  );

  // Días "No opera" por (fecha, turno) — design horario-turno-dia §5/§6: la
  // celda queda deshabilitada, sin importar si hay o no una asignación
  // atrapada de antes del cambio de horario (S6b, fuera de este slice).
  const noOperaPorCelda = useMemo(() => {
    const set = new Set<string>();
    for (const h of data.horarios_por_fecha) {
      if (h.no_opera) set.add(`${h.fecha}|${h.turno_id}`);
    }
    return set;
  }, [data.horarios_por_fecha]);

  return (
    <Card>
      {/* max-h + overflow-auto: el scroll vertical vive en el contenedor para
          que el thead sticky mantenga los días visibles (mismo patrón que la
          matriz del detalle C4). */}
      <CardContent className="max-h-[calc(100vh-180px)] overflow-auto p-0">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20">
            <tr className="bg-surface-overlay">
              <th className="sticky left-0 z-30 border-b border-r border-border bg-surface-overlay px-3 py-2 text-left">
                <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                  Línea
                </span>
              </th>
              {data.dias.map((fecha) => {
                const esHoy = fecha === data.fecha_hoy;
                return (
                  <th
                    key={fecha}
                    className={cn(
                      'border-b border-l border-border bg-surface-overlay px-2 py-2 text-center',
                      esHoy && 'bg-accent-soft/40 text-fg',
                    )}
                  >
                    <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                      {diaSemanaCorto(fecha)} {fecha.slice(8, 10)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.lineas.map((l) => (
              <tr key={l.id}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 border-b border-r border-border bg-surface-base px-3 py-2 text-left align-top"
                >
                  <div className="text-sm font-medium text-fg">{l.nombre}</div>
                  <div className="text-[10.5px] text-fg-tertiary">{l.planta_nombre}</div>
                </th>
                {data.dias.map((fecha) => (
                  <td key={fecha} className="border-b border-l border-border p-0 align-top">
                    <div className="flex flex-col">
                      {turnosOrdenados.map((t) => {
                        const prog = progPorCelda.get(celdaKey(fecha, t.id, l.id));
                        const enConflicto = prog
                          ? conflictosByKey.has(`${fecha}|${t.id}|${prog.lider_user_id}`)
                          : false;
                        const noOpera = noOperaPorCelda.has(`${fecha}|${t.id}`);
                        return (
                          <SubCeldaTurno
                            key={t.id}
                            fecha={fecha}
                            turnoId={t.id}
                            turnoNumero={t.numero}
                            turnoNombre={t.nombre}
                            turnoHoraInicio={t.hora_inicio}
                            turnoHoraFin={t.hora_fin}
                            lineaId={l.id}
                            lineaCodigo={l.codigo}
                            lineaNombre={l.nombre}
                            prog={prog}
                            enConflicto={enConflicto}
                            noOpera={noOpera}
                            conflictosByKey={conflictosByKey}
                            programaciones={data.programaciones}
                            lineasById={data.lineas}
                            onQuitar={onQuitar}
                          />
                        );
                      })}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function SubCeldaTurno({
  fecha,
  turnoId,
  turnoNumero,
  turnoNombre,
  turnoHoraInicio,
  turnoHoraFin,
  lineaId,
  lineaCodigo,
  lineaNombre,
  prog,
  enConflicto,
  noOpera,
  conflictosByKey,
  programaciones,
  lineasById,
  onQuitar,
}: {
  fecha: string;
  turnoId: string;
  turnoNumero: number;
  turnoNombre: string;
  turnoHoraInicio: string;
  turnoHoraFin: string;
  lineaId: string;
  lineaCodigo: string;
  lineaNombre: string;
  prog: ProgramacionLiderEnriquecida | undefined;
  enConflicto: boolean;
  /** Día "No opera" para este (turno, fecha) — design horario-turno-dia §5/§6. */
  noOpera: boolean;
  conflictosByKey: Set<string>;
  programaciones: ProgramacionLiderEnriquecida[];
  lineasById: CoordinadorProgramacionSemana['lineas'];
  onQuitar: (progId: string) => void;
}) {
  const dropId = `drop-${fecha}|${turnoId}|${lineaId}`;
  // Deshabilitar el drop en un día "No opera" (celda vacía): defensa en el
  // cliente además del 422 del backend (design §5). No aplica a celdas ya
  // asignadas (S6b decide qué pasa con una asignación atrapada; fuera de
  // este slice, ver design §5 nota).
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dropId,
    disabled: noOpera && !prog,
  });

  // La doble asignación NO tiñe la celda: es válida. Solo se señala en el chip
  // y en el popover como dato informativo.
  const borderTone = prog ? 'border-l-ok' : 'border-l-border';

  if (prog) {
    // Celda asignada con popover de detalle
    const iniciales = `${prog.lider_nombre[0] ?? ''}${prog.lider_apellido[0] ?? ''}`.toUpperCase();
    // Otras líneas del mismo líder ese día (para mostrar en popover)
    const otrasAsignacionesDia = programaciones.filter(
      (p) => p.fecha === fecha && p.lider_user_id === prog.lider_user_id && p.id !== prog.id,
    );
    return (
      <div
        ref={setDropRef}
        className={cn(
          'border-l-2 border-b border-border px-1.5 py-1 transition-colors last:border-b-0',
          borderTone,
          isOver && 'bg-accent-soft/40',
        )}
      >
        <Popover
          width={300}
          trigger={
            <DraggableProgChip progId={prog.id} iniciales={iniciales} enConflicto={enConflicto} />
          }
          content={
            <CeldaPopoverContent
              prog={prog}
              turnoNumero={turnoNumero}
              turnoNombre={turnoNombre}
              turnoHoraInicio={turnoHoraInicio}
              turnoHoraFin={turnoHoraFin}
              lineaCodigo={lineaCodigo}
              lineaNombre={lineaNombre}
              otrasAsignaciones={otrasAsignacionesDia}
              lineasById={lineasById}
              enConflicto={enConflicto}
              conflictosByKey={conflictosByKey}
              onQuitar={() => onQuitar(prog.id)}
            />
          }
        />
      </div>
    );
  }

  // Celda vacía en día "No opera": deshabilitada, sin drop, con tooltip
  // explicativo (design horario-turno-dia §6).
  if (noOpera) {
    return (
      <div
        aria-disabled="true"
        className="flex items-center gap-1 border-b border-l-2 border-border bg-surface-overlay/30 px-1.5 py-1 text-[10px] text-fg-tertiary/40"
      >
        <span className="font-mono">T{turnoNumero}</span>
        <CeldaNoOperaTooltip turnoNombre={turnoNombre} />
      </div>
    );
  }

  // Celda vacía: drop zone visible
  return (
    <div
      ref={setDropRef}
      className={cn(
        'flex items-center gap-1 border-b border-l-2 border-border px-1.5 py-1 text-[10px] transition-colors last:border-b-0',
        borderTone,
        isOver
          ? 'bg-accent-soft/40 text-accent'
          : 'text-fg-tertiary/50 hover:bg-surface-overlay/40',
      )}
    >
      <span className="font-mono">T{turnoNumero}</span>
    </div>
  );
}

function DraggableProgChip({
  progId,
  iniciales,
  enConflicto,
}: {
  progId: string;
  iniciales: string;
  enConflicto: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `prog-${progId}`,
  });
  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      type="button"
      className={cn(
        'inline-flex w-full cursor-grab items-center justify-start gap-1 rounded-sm bg-surface-overlay/70 px-1 py-0.5 text-[10.5px] font-medium tabular-nums transition-colors hover:bg-surface-overlay active:cursor-grabbing',
        'text-fg',
        isDragging && 'opacity-30',
      )}
    >
      {/* Marca informativa de doble asignación (líder en 2+ líneas del turno) */}
      {enConflicto ? (
        <span className="text-fg-tertiary" title="Programado en más de una línea este turno">
          ⧉
        </span>
      ) : null}
      <span>{iniciales}</span>
    </button>
  );
}

function CeldaPopoverContent({
  prog,
  turnoNumero,
  turnoNombre,
  turnoHoraInicio,
  turnoHoraFin,
  lineaCodigo,
  lineaNombre,
  otrasAsignaciones,
  lineasById,
  enConflicto,
  conflictosByKey,
  onQuitar,
}: {
  prog: ProgramacionLiderEnriquecida;
  turnoNumero: number;
  turnoNombre: string;
  turnoHoraInicio: string;
  turnoHoraFin: string;
  lineaCodigo: string;
  lineaNombre: string;
  otrasAsignaciones: ProgramacionLiderEnriquecida[];
  lineasById: CoordinadorProgramacionSemana['lineas'];
  enConflicto: boolean;
  conflictosByKey: Set<string>;
  onQuitar: () => void;
}) {
  // Líneas del líder en este turno (incluyendo la actual) si hay conflicto
  const lineasMismoTurno = enConflicto
    ? otrasAsignaciones
        .filter((p) => p.turno_id === prog.turno_id)
        .map((p) => lineasById.find((l) => l.id === p.linea_id))
        .filter((l): l is CoordinadorProgramacionSemana['lineas'][number] => !!l)
    : [];
  // Marcar variable como usada para que tsc -b no se queje
  void conflictosByKey;

  return (
    <div className="flex flex-col gap-2 text-xs">
      <div>
        <p className="text-sm font-medium text-fg">
          {prog.lider_nombre} {prog.lider_apellido}
        </p>
        <p className="text-[11px] text-fg-tertiary">
          {formatearFechaCorta(prog.fecha)} · {turnoNombre} (T{turnoNumero}{' '}
          <span className="font-mono">
            {turnoHoraInicio.slice(0, 5)}–{turnoHoraFin.slice(0, 5)}
          </span>
          )
        </p>
      </div>
      <div className="text-[11px] text-fg-secondary">
        <strong className="text-fg">Línea actual:</strong> {lineaCodigo} {lineaNombre}
      </div>
      {otrasAsignaciones.length > 0 ? (
        <div className="border-t border-border pt-2 text-[11px] text-fg-tertiary">
          Otras asignaciones hoy: {otrasAsignaciones.length}
        </div>
      ) : null}
      {enConflicto ? (
        <div className="rounded-sm border border-border bg-surface-overlay/50 px-2 py-1 text-[11px] text-fg-secondary">
          ⧉ Doble asignación: este líder cubre {lineasMismoTurno.length + 1} líneas en este turno.
          También en: {lineasMismoTurno.map((l) => l.codigo).join(', ')}. Captura una a la vez desde
          el selector.
        </div>
      ) : null}
      <div className="flex justify-end gap-1.5 border-t border-border pt-2">
        <Button variant="ghost" size="sm" onClick={onQuitar}>
          Quitar
        </Button>
        <Button variant="outline" size="sm" disabled title="Arrastre la card a otra celda">
          Cambiar
        </Button>
      </div>
    </div>
  );
}

// Asegurar uso del type para tsc estricto (evita unused warning si llega a moverse)
void ({} as ConflictoProgramacion);
